import {ActivatedRouteSnapshot, ResolveFn, Router} from '@angular/router';
import {inject} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslateService} from '@ngx-translate/core';
import {catchError, map, of} from 'rxjs';
import {InvoiceDocumentStore} from '../state/invoice-document.store';
import {UserStore} from '../state/user.store';

export const invoiceDocumentResolver: ResolveFn<boolean> = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const store = inject(InvoiceDocumentStore);

  const uuid = route.paramMap.get('invoiceDocumentUuid');
  if (!uuid) {
    void router.navigate(['/not-found']);
    return of(false);
  }

  return store.loadInvoiceDocumentAndReturn(uuid).pipe(
    map(() => true),
    catchError((err) => {
      if (err.status === 404) {
        void router.navigate(['/not-found']);
      } else {
        void router.navigate(['/error']);
      }
      return of(false);
    })
  );
};

/**
 * Loads the invoice document (single request, cache-aware via the store) and
 * checks in the same pass whether the current user's services grant access to
 * the validation view, redirecting to the non-validation route otherwise.
 * A network error is treated as an error, not as a denied access.
 */
export const invoiceDocumentValidationAccessResolver: ResolveFn<boolean> = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const store = inject(InvoiceDocumentStore);
  const userStore = inject(UserStore);
  const snackBar = inject(MatSnackBar);
  const translate = inject(TranslateService);

  const uuid = route.paramMap.get('invoiceDocumentUuid');
  if (!uuid) {
    void router.navigate(['/not-found']);
    return of(false);
  }

  return store.loadInvoiceDocumentAndReturn(uuid).pipe(
    map((invoice) => {
      const userServices = userStore.currentUserServices() ?? [];
      if (userServices.some(s => s === invoice.service)) {
        return true;
      }

      snackBar.open(translate.instant('permission.unauthorized'));
      void router.navigate(['/invoices', uuid]);
      return false;
    }),
    catchError((err) => {
      if (err.status === 404) {
        void router.navigate(['/not-found']);
      } else {
        void router.navigate(['/error']);
      }
      return of(false);
    })
  );
};
